import React, { useEffect, useRef, useState } from "react";
import { FaExclamationCircle } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { uploadShepCertificate, getShepCertificateMetadata } from "../../common/api/ofdApi";
import AlertBox from "../../shared/Ui/AlertBox/AlertBox";
import TableLoader from "../../shared/Ui/Loader/TableLoader";
import ModalComponent from "../../shared/Ui/ModalComponent/ModalWindow";
import ModalButton from "../../shared/Ui/Buttons/ModalButtons";

interface ICertMeta {
  id: number;
  issuerName: string;
  serialNumber: string;
  validFrom: string;
  validTo: string;
  createdBy: string;
  createdAt: string;
}

const OfdCertificate: React.FC = () => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [password, setPassword] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [meta, setMeta] = useState<ICertMeta | null>(null);
  const [alertOpen, setAlertOpen] = useState<any>(null);

  const fetchMeta = async () => {
    setIsLoading(true);
    try {
      const response = await getShepCertificateMetadata();
      setMeta(response ?? null);
    } catch (error: any) {
      setMeta(null);
      setAlertOpen({
        status: "error",
        message: error?.response?.data?.message || t("ofd.meta_fetch_error"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMeta();
  }, []);

  const handleUpload = async () => {
    if (!file) {
      setAlertOpen({ status: "error", message: t("ofd.select_certificate_file") });
      return;
    }
    if (!password.trim()) {
      setAlertOpen({ status: "error", message: t("ofd.enter_certificate_password") });
      return;
    }

    setIsUploading(true);
    try {
      await uploadShepCertificate(file, password.trim());
      setAlertOpen({ status: "SUCCESS", message: t("ofd.certificate_upload_success") });
      setFile(null);
      setPassword("");
      await fetchMeta();
    } catch (error: any) {
      setAlertOpen({
        status: "error",
        message: error?.response?.data?.message || t("ofd.certificate_upload_error"),
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleUploadFromModal = async () => {
    await handleUpload();
    setIsUploadModalOpen(false);
  };

  const handleDropFile = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(false);
    const droppedFile = event.dataTransfer.files?.[0];
    if (droppedFile) {
      setFile(droppedFile);
    }
  };

  return (
    <div className="flex flex-1 flex-col min-w-0">
      <h2 className="my-3 text-xl font-semibold">{t("ofd.certificate_upload")}</h2>

      <div className="w-full my-3">
        <button
          type="button"
          onClick={() => setIsUploadModalOpen(true)}
          className="bg-mainColor text-white font-semibold py-2 px-4 h-[40px] rounded whitespace-nowrap"
        >
          {t("ofd.upload_key")}
        </button>
      </div>

      <div className="w-full">
        <div className="rounded-lg overflow-x-auto bg-white">
          <table className="max-w-full w-full leading-normal">
            <thead className="border-b-2 border-gray-300">
              <tr>
                <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">{t("ofd.table_id")}</th>
                <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">{t("ofd.table_issuer")}</th>
                <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">{t("ofd.table_serial")}</th>
                <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">{t("ofd.table_valid_from")}</th>
                <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">{t("ofd.table_valid_to")}</th>
                <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">{t("ofd.table_created_by")}</th>
                <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">{t("ofd.table_created_at")}</th>
              </tr>
            </thead>
            {isLoading ? (
              <tbody>
                <tr>
                  <td colSpan={7} className="text-center py-10">
                    <TableLoader />
                  </td>
                </tr>
              </tbody>
            ) : !meta ? (
              <tbody>
                <tr>
                  <td colSpan={7} className="text-center py-8 text-gray-500">
                    <div className="flex items-center justify-center gap-2">
                      <FaExclamationCircle />
                      {t("ofd.meta_not_found")}
                    </div>
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody className="font-normal text-center">
                <tr>
                  <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">{meta.id}</td>
                  <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">{meta.issuerName || "-"}</td>
                  <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">{meta.serialNumber || "-"}</td>
                  <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">{meta.validFrom || "-"}</td>
                  <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">{meta.validTo || "-"}</td>
                  <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">{meta.createdBy || "-"}</td>
                  <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">{meta.createdAt || "-"}</td>
                </tr>
              </tbody>
            )}
          </table>
        </div>
      </div>

      <ModalComponent
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        headerContent={<div className="text-base font-semibold">{t("ofd.upload_certificate")}</div>}
        content={
          <div className="w-full flex flex-col gap-4 mt-3">
            <div className="flex flex-col gap-1">
              <label className="text-sm text-gray-700 font-medium">{t("ofd.certificate_password")}*</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-10 border border-mainColor rounded-lg px-3 text-[#2e3138] outline-none"
                placeholder={t("ofd.certificate_password_placeholder")}
              />
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept=".p12,.pfx,application/x-pkcs12"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="hidden"
            />

            <div
              onDragOver={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setIsDragActive(true);
              }}
              onDragLeave={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setIsDragActive(false);
              }}
              onDrop={handleDropFile}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition ${
                isDragActive ? "border-mainColor bg-blue-50" : "border-[#a3c0d2] bg-[#f9fbff]"
              }`}
            >
              <p className="text-sm font-medium text-gray-700">
                {t("ofd.drag_drop_hint")}
              </p>
              <p className="text-xs text-gray-500 mt-1">{t("ofd.supported_formats")}</p>
              <p className="text-sm text-mainColor mt-3 break-all">
                {file ? t("ofd.file_selected", { name: file.name }) : t("ofd.file_not_selected")}
              </p>
            </div>
          </div>
        }
        customContent={
          <div className="flex gap-[10px]">
            <ModalButton type="button" onClick={() => setIsUploadModalOpen(false)}>
              {t("mainButtons.cancel")}
            </ModalButton>
            <ModalButton
              type="button"
              color="green"
              onClick={handleUploadFromModal}
            >
              {isUploading ? t("ofd.uploading") : t("ofd.upload")}
            </ModalButton>
          </div>
        }
      />

      <AlertBox isOpen={alertOpen} setIsOpen={setAlertOpen} />
    </div>
  );
};

export default OfdCertificate;
